import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './i18n';

// Auth
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ChangePasswordModal from './components/auth/ChangePasswordModal';
import CompanySelector from './components/auth/CompanySelector';

// Shared
import Navigation from './components/shared/Navigation';

// Admin
import Services from './components/admin/Services';
import AuditLogs from './components/admin/AuditLogs';

// Operational
import Agenda from './components/operational/Agenda';

// Marketplace
import Marketplace from './components/marketplace/Marketplace';

// Placeholder components
import Dashboard from './components/shared/Dashboard';
import RootDashboard from './components/root/RootDashboard';
import Reservations from './components/operational/Reservations';
import Clients from './components/operational/Clients';
import Payments from './components/operational/Payments';
import CollaboratorFinance from './components/operational/CollaboratorFinance';
import Reports from './components/admin/Reports';
import CheckInOutPage from './components/operational/CheckInOutPage';
import Companies from './components/root/Companies';
import Collaborators from './components/operational/Collaborators';
import CollaboratorScheduler from './components/operational/CollaboratorScheduler';
import PointOfSale from './components/operational/PointOfSale';
import Establishments from './components/operational/Establishments';
import OrdersBoard from './components/operational/OrdersBoard';
import Settings from './components/shared/Settings';
import CustomerApp from './components/customer/CustomerApp';
import PublicOrder from './components/customer/PublicOrder';
import VoucherPage from './components/customer/VoucherPage';
import LoadingState from './components/shared/LoadingState';
import { getAuthenticatedHomePath } from './utils/collaboratorPermissions';

import './App.css';
import Landing from './components/landing/Landing';

const VoucherValidator = lazy(() => import('./components/operational/VoucherValidator'));

const AppLayout = ({ children }) => {
  const { currentUser, mustChangePassword } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const location = useLocation();
  const isCustomerRoute = location.pathname.startsWith('/cliente');
  const isPublicOrderRoute = location.pathname.startsWith('/pedido');
  const isVoucherRoute = location.pathname.startsWith('/voucher');

  useEffect(() => {
    if (currentUser && mustChangePassword) {
      // Verificar se usuário usa Google Auth
      const isGoogleUser = currentUser.providerData.some(p => p.providerId === 'google.com');
      
      // Usuários Google não precisam trocar senha
      if (!isGoogleUser) {
        setShowPasswordModal(true);
      }
    }
  }, [currentUser, mustChangePassword]);

  const handlePasswordChanged = () => {
    setShowPasswordModal(false);
    window.location.reload(); // Recarregar para atualizar dados do usuário
  };

  if (!currentUser || isCustomerRoute || isPublicOrderRoute || isVoucherRoute) {
    return children;
  }

  return (
    <>
      {showPasswordModal && (
        <ChangePasswordModal onSuccess={handlePasswordChanged} />
      )}
      <div className="app-layout">
        <Navigation isCollapsed={isNavCollapsed} onToggle={() => setIsNavCollapsed(prev => !prev)} />
        <main className={`main-content ${isNavCollapsed ? 'main-content--collapsed' : ''}`}>
          {children}
        </main>
      </div>
    </>
  );
};

const DashboardRouter = () => {
  const { userData } = useAuth();

  if (userData?.role === 'collaborator') {
    return <Navigate to={getAuthenticatedHomePath(userData)} replace />;
  }
  
  if (userData?.role === 'root') {
    return <RootDashboard />;
  }
  
  return <Dashboard />;
};

const HomeOrLanding = () => {
  const { currentUser } = useAuth();
  if (currentUser) {
    return (
      <ProtectedRoute>
        <DashboardRouter />
      </ProtectedRoute>
    );
  }
  return <Landing />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppLayout>
          <Routes>
            <Route path="/login" element={<Login />} />
            
            <Route path="/select-company" element={
              <ProtectedRoute allowedRoles={['collaborator']}>
                <CompanySelector />
              </ProtectedRoute>
            } />
            
            <Route path="/" element={<HomeOrLanding />} />

            <Route path="/marketplace" element={
              <ProtectedRoute allowedRoles={['operational']}>
                <Marketplace />
              </ProtectedRoute>
            } />

            <Route path="/agenda" element={
              <ProtectedRoute allowedRoles={['admin', 'operational', 'collaborator']} requiredPermission="reservations">
                <Agenda />
              </ProtectedRoute>
            } />

            <Route path="/reservations" element={
              <ProtectedRoute allowedRoles={['admin', 'operational', 'collaborator']} requiredPermission="reservations">
                <Reservations />
              </ProtectedRoute>
            } />

            <Route path="/agendamento" element={
              <ProtectedRoute allowedRoles={['admin', 'operational', 'collaborator']} requiredPermission="scheduler">
                <CollaboratorScheduler />
              </ProtectedRoute>
            } />

            <Route path="/validar-voucher" element={
              <ProtectedRoute allowedRoles={['admin', 'collaborator']} requiredPermission="voucher_validation">
                <Suspense fallback={<LoadingState title="Carregando leitor" subtitle="Preparando a câmera" />}>
                  <VoucherValidator />
                </Suspense>
              </ProtectedRoute>
            } />

            <Route path="/services" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Services />
              </ProtectedRoute>
            } />

            <Route path="/check/:companyId/:reservationId/:action" element={
              <ProtectedRoute allowedRoles={['admin', 'collaborator']} requiredPermission="voucher_validation">
                <CheckInOutPage />
              </ProtectedRoute>
            } />
            <Route path="/check/:companyId/:reservationId/:action/:token" element={
              <ProtectedRoute allowedRoles={['admin', 'collaborator']} requiredPermission="voucher_validation">
                <CheckInOutPage />
              </ProtectedRoute>
            } />
            <Route path="/voucher/:token" element={<VoucherPage />} />
            <Route path="/cliente/:companySlug" element={<CustomerApp />} />
            <Route path="/pedido/:companySlug/:establishmentId" element={<PublicOrder />} />

            <Route path="/clients" element={
              <ProtectedRoute allowedRoles={['admin', 'operational']}>
                <Clients />
              </ProtectedRoute>
            } />

            <Route path="/collaborators" element={
              <ProtectedRoute allowedRoles={['admin', 'operational']}>
                <Collaborators />
              </ProtectedRoute>
            } />

            <Route path="/payments" element={
              <ProtectedRoute allowedRoles={['admin', 'operational']}>
                <Payments />
              </ProtectedRoute>
            } />

            <Route path="/meu-financeiro" element={
              <ProtectedRoute allowedRoles={['collaborator']} requiredPermission="scheduler">
                <CollaboratorFinance />
              </ProtectedRoute>
            } />

            <Route path="/establishments" element={
              <ProtectedRoute allowedRoles={['admin', 'operational']}>
                <Establishments />
              </ProtectedRoute>
            } />

            <Route path="/orders" element={
              <ProtectedRoute allowedRoles={['admin', 'operational', 'collaborator']} requiredPermission="orders">
                <OrdersBoard />
              </ProtectedRoute>
            } />

            <Route path="/pos" element={
              <ProtectedRoute allowedRoles={['admin', 'operational', 'collaborator']} requiredPermission="pos">
                <PointOfSale />
              </ProtectedRoute>
            } />

            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Reports />
              </ProtectedRoute>
            } />

            <Route path="/audit" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AuditLogs />
              </ProtectedRoute>
            } />

            <Route path="/companies" element={
              <ProtectedRoute allowedRoles={['root']}>
                <Companies />
              </ProtectedRoute>
            } />

            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={['root', 'admin', 'operational']}>
                <Settings />
              </ProtectedRoute>
            } />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </AppLayout>
      </Router>
    </AuthProvider>
  );
}

export default App;
